import React, { useMemo, useState } from "react";
import { MessageCircle, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const initialMessages = [
  {
    id: "welcome",
    role: "assistant",
    text: "Hello! Welcome to Barangay Help Chat. I can help with document requests, concern reporting, and office information.",
  },
];

export const FloatingChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState(initialMessages);

  const canSend = useMemo(() => draft.trim().length > 0, [draft]);

  const onSubmit = (event) => {
    event.preventDefault();
    if (!canSend) return;

    const text = draft.trim();
    const userMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      text,
    };
    const placeholderReply = {
      id: `assistant-${Date.now()}`,
      role: "assistant",
      text: "Thank you for your message. A barangay staff member will review your concern and assist you shortly.",
    };

    setMessages((prev) => [...prev, userMessage, placeholderReply]);
    setDraft("");
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end" data-testid="floating-chat-widget">
      {isOpen && (
        <section className="mb-3 w-[calc(100vw-2rem)] max-w-sm rounded-2xl border bg-background shadow-2xl">
          <header className="flex items-center justify-between border-b px-4 py-3">
            <div>
              <p className="text-sm font-semibold">Barangay Help Chat</p>
              <p className="text-xs text-muted-foreground">Quick help for residents</p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
            >
              <X className="h-4 w-4" />
            </Button>
          </header>

          <div className="max-h-80 min-h-72 space-y-3 overflow-y-auto px-4 py-3">
            <div className="flex flex-wrap gap-2">
              {["Request a document", "Track a request", "Emergency numbers"].map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  onClick={() => setDraft(prompt)}
                  className="rounded-full border bg-secondary px-3 py-1 text-xs text-secondary-foreground hover:bg-secondary/80 transition"
                >
                  {prompt}
                </button>
              ))}
            </div>
            {messages.map((message) => (
              <div
                key={message.id}
                className={`max-w-[90%] rounded-xl px-3 py-2 text-sm ${
                  message.role === "assistant"
                    ? "bg-secondary text-secondary-foreground"
                    : "ml-auto bg-primary text-primary-foreground"
                }`}
              >
                {message.text}
              </div>
            ))}
          </div>

          <form onSubmit={onSubmit} className="flex gap-2 border-t p-3">
            <Input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Type your question..."
              aria-label="Chat message"
            />
            <Button type="submit" size="icon" disabled={!canSend} aria-label="Send message">
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </section>
      )}

      <Button
        type="button"
        size="icon"
        className="h-12 w-12 rounded-full shadow-lg"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={isOpen ? "Close chat widget" : "Open chat widget"}
      >
        {isOpen ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
      </Button>
    </div>
  );
};
